import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Chip, Tooltip, Button, useMediaQuery, useTheme, Stack, Divider } from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { arSD } from '@mui/x-data-grid/locales';
import {
  Visibility, TableChart, Download, PictureAsPdf,
  Image as ImageIcon, Link as LinkIcon,
} from '@mui/icons-material';
import { formatDate } from '../../lib/dateUtils';
import { exportRowsToCsv, exportRowsToPdf } from '../../lib/exportUtils';
import { arabicEntityLabel, arabicColumnLabel } from '../../lib/columnLabels';
import { enumChip } from '../../lib/enumLabels';
import { stripHtml } from '../../lib/htmlUtils';
import type { AgentEntityRoute } from '../../lib/agentApi';

interface AgentResultTableProps {
  entity: string | null;
  rows: any[];
  columns: string[];
  rowCount: number;
  route: AgentEntityRoute | null;
}

const isDateField = (key: string) =>
  key.includes('date') || key.endsWith('_at') || key.endsWith('Date');

// Technical id / uuid / actor columns we hide from end users (kept in the row
// data so the "View" button can still navigate). pcu_id is a real reference, kept.
const isHiddenColumn = (key: string) =>
  key === 'id' ||
  (key.endsWith('_id') && key !== 'pcu_id') ||
  ['created_by', 'updated_by', 'uploaded_by', 'source_slide_id'].includes(key);

const prettyHeader = (key: string) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const HTML_TAG_RE = /<\/?[a-z][\s\S]*>/i;

// Render any cell value safely (objects/arrays -> compact JSON, null -> em
// dash, rich-text HTML -> plain-text preview so raw tags never leak into the
// grid).
const renderValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  const s = String(value);
  return HTML_TAG_RE.test(s) ? stripHtml(s) : s;
};

// A cell holding a URL (attachment, image, external link) -> open in a new tab.
const isUrlValue = (v: any): v is string => typeof v === 'string' && /^https?:\/\//i.test(v.trim());

const isImageUrl = (v: string) => /\.(png|jpe?g|gif|webp|svg|bmp)(\?|$)/i.test(v);
const isPdfUrl = (v: string) => /\.pdf(\?|$)/i.test(v);

// Short, friendly label for a file/attachment URL (decoded filename).
const fileLabel = (url: string): string => {
  try {
    const last = new URL(url).pathname.split('/').filter(Boolean).pop() || url;
    const name = decodeURIComponent(last);
    return name.length > 34 ? name.slice(0, 34) + '…' : name;
  } catch {
    return url.length > 34 ? url.slice(0, 34) + '…' : url;
  }
};

// Related-entity linking: a visible "name" column paired with a hidden foreign
// key (e.g. `company` + `company_id`) becomes a link to that related record.
const FK_PREFIX_ROUTE: Record<string, { detail?: string; list: string }> = {
  company: { detail: '/companies/:id', list: '/companies' },
  contractor: { detail: '/companies/:id', list: '/companies' },
  partner: { detail: '/partners/:id', list: '/partners' },
  tender: { detail: '/tenders/:id', list: '/tenders' },
  contract: { detail: '/contracts/:id', list: '/contracts' },
  meeting: { list: '/board-meetings' },
  album_name: { list: '/gallery' },
  city: { list: '/areas-cities' },
  area: { list: '/areas-cities' },
  classification: { list: '/classifications' },
};

// Given a visible column, find a related-record path from a sibling *_id value.
const relatedPath = (key: string, row: any): string | null => {
  const base = key.replace(/_(name|translation)$/, '');
  const cfg = FK_PREFIX_ROUTE[base];
  if (!cfg) return null;
  const fkId = row[`${base}_id`];
  if (fkId == null || fkId === '') return null;
  return cfg.detail ? cfg.detail.replace(':id', String(fkId)) : cfg.list;
};

const AgentResultTable: React.FC<AgentResultTableProps> = ({ entity, rows, columns, rowCount, route }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const theme = useTheme();
  // On phones a data grid is unreadable — render each row as an item card instead.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (rowCount === 0) {
    return (
      <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1 }}>
        <TableChart fontSize="small" color="disabled" />
        <Typography variant="body2" color="text.secondary">
          {t('agent.noResults')}
        </Typography>
      </Box>
    );
  }

  const idField = route?.idField || 'id';

  // Ensure every row has a stable id for DataGrid.
  const gridRows = rows.map((row, index) => ({
    ...row,
    __rowId: row[idField] ?? index,
  }));

  const canView = !!route && rows.some((r) => r[idField] != null);

  const buildPath = (row: any): string | null => {
    if (!route) return null;
    const id = row[idField];
    if (route.detail && id != null) return route.detail.replace(':id', String(id));
    return route.list;
  };

  // Columns actually shown to users (ids hidden). Fall back to all if empty.
  const visibleColumns = columns.filter((c) => !isHiddenColumn(c));
  const shownColumns = visibleColumns.length ? visibleColumns : columns;

  // The main "name" column becomes a clickable link that opens the record.
  const titleKey = shownColumns.find((c) =>
    ['name', 'title', 'tender_name', 'meeting_no', 'label', 'page', 'filename'].includes(c)) || shownColumns[0];

  const dataColumns: GridColDef[] = shownColumns.map((key) => ({
    field: key,
    headerName: isRTL ? arabicColumnLabel(key) : prettyHeader(key),
    flex: 1,
    minWidth: 130,
    sortable: true,
    valueFormatter: isDateField(key)
      ? (value: any) => formatDate(value, isRTL ? 'ar' : 'en', 'medium')
      : undefined,
    renderCell: isDateField(key)
      ? undefined
      : (params) => {
          const text = renderValue(params.value);

          // 0) Boolean -> translated Yes/No chip (never raw true/false).
          if (typeof params.value === 'boolean') {
            return (
              <Chip
                size="small"
                label={params.value ? t('agent.yes', { defaultValue: 'Yes' }) : t('agent.no', { defaultValue: 'No' })}
                color={params.value ? 'success' : 'default'}
                variant="outlined"
              />
            );
          }

          // 0b) Known enum column (tenders.type, fund_system.payment_status, ...) -> translated chip.
          const chip = enumChip(key, params.value, isRTL ? 'ar' : 'en');
          if (chip) {
            return <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />;
          }

          // 1) Attachment / image / external URL -> open the file in a new tab.
          if (isUrlValue(params.value)) {
            const url = params.value.trim();
            const Icon = isPdfUrl(url) ? PictureAsPdf : isImageUrl(url) ? ImageIcon : LinkIcon;
            return (
              <Tooltip title={t('agent.openFile')} placement="top">
                <Box
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0,
                    color: 'primary.main', textDecoration: 'underline', textUnderlineOffset: '2px',
                  }}
                >
                  <Icon sx={{ fontSize: 15, flexShrink: 0 }} />
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {fileLabel(url)}
                  </Box>
                </Box>
              </Tooltip>
            );
          }

          // 2) Related entity (e.g. company / city) -> link to that record.
          const relPath = relatedPath(key, params.row);
          if (relPath && params.value != null && params.value !== '') {
            return (
              <Tooltip title={t('agent.openRelated')} placement="top">
                <Box component="span" onClick={(e) => { e.stopPropagation(); navigate(relPath); }}
                  sx={{
                    color: 'primary.main', cursor: 'pointer', textDecoration: 'underline dotted',
                    textUnderlineOffset: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                  {text}
                </Box>
              </Tooltip>
            );
          }

          const path = key === titleKey ? buildPath(params.row) : null;
          if (path) {
            return (
              <Tooltip title={t('agent.openRecord')} placement="top">
                <Box
                  component="span"
                  onClick={(e) => { e.stopPropagation(); navigate(path); }}
                  sx={{
                    color: 'primary.main', cursor: 'pointer', fontWeight: 600,
                    textDecoration: 'underline', textUnderlineOffset: '2px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    '&:hover': { color: 'primary.dark' },
                  }}
                >
                  {text}
                </Box>
              </Tooltip>
            );
          }
          return (
            <Tooltip title={text} placement="top">
              <span>{text}</span>
            </Tooltip>
          );
        },
  }));

  const gridColumns: GridColDef[] = canView
    ? [
        ...dataColumns,
        {
          field: '__actions',
          type: 'actions',
          headerName: t('table.actions'),
          width: 90,
          getActions: (params) => {
            const path = buildPath(params.row);
            if (!path) return [];
            return [
              <GridActionsCellItem
                key="view"
                icon={<Visibility fontSize="small" />}
                label={t('agent.view')}
                onClick={() => navigate(path)}
                showInMenu={false}
              />,
            ];
          },
        },
      ]
    : dataColumns;

  const entityLabel = entity
    ? (isRTL ? arabicEntityLabel(entity) : t(`nav.${entity}`, { defaultValue: prettyHeader(entity) }))
    : t('agent.results');
  const filenameBase = `PCU_${entity || 'results'}`;

  return (
    <Paper variant="outlined" sx={{ mt: 1.5, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
        <TableChart fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {entityLabel}
        </Typography>
        <Chip size="small" label={t('agent.rowCount', { count: rowCount })} variant="outlined" sx={{ height: 22, bgcolor: 'background.paper' }} />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={t('agent.downloadCsv')}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Download fontSize="small" />}
            onClick={() => exportRowsToCsv(rows, shownColumns, filenameBase)}
            sx={{ minWidth: 0, py: 0.25, px: { xs: 0.75, sm: 1.25 }, '& .MuiButton-startIcon': { m: { xs: 0, sm: undefined } } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>CSV</Box>
          </Button>
        </Tooltip>
        <Tooltip title={t('agent.downloadPdf')}>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<PictureAsPdf fontSize="small" />}
            onClick={() =>
              exportRowsToPdf(rows, shownColumns, {
                // Reports are Arabic by default.
                title: arabicEntityLabel(entity),
                subtitle: t('agent.rowCount', { count: rowCount }),
                filenameBase,
              })
            }
            sx={{ minWidth: 0, py: 0.25, px: { xs: 0.75, sm: 1.25 }, '& .MuiButton-startIcon': { m: { xs: 0, sm: undefined } } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>PDF</Box>
          </Button>
        </Tooltip>
      </Box>
      {isMobile ? (
        // ── Mobile: one readable card per row (title + key fields + View) ──
        <Box sx={{ maxHeight: 460, overflowY: 'auto', bgcolor: 'background.paper' }}>
          <Stack divider={<Divider />}>
            {gridRows.map((row, i) => {
              const restKeys = shownColumns.filter((c) => c !== titleKey).slice(0, 5);
              const path = buildPath(row);
              return (
                <Box key={row.__rowId ?? i} onClick={() => { if (path) navigate(path); }}
                  sx={{ p: 1.25, ...(path && { cursor: 'pointer', '&:active': { bgcolor: 'action.hover' } }) }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{
                      fontWeight: 700, flexGrow: 1, wordBreak: 'break-word',
                      ...(path && { color: 'primary.main', textDecoration: 'underline', textUnderlineOffset: '2px' }),
                    }}>
                      {renderValue(row[titleKey])}
                    </Typography>
                    {canView && path && (
                      <Button size="small" variant="text" startIcon={<Visibility sx={{ fontSize: 16 }} />}
                        onClick={(e) => { e.stopPropagation(); navigate(path); }} sx={{ flexShrink: 0, minWidth: 0 }}>
                        {t('agent.view')}
                      </Button>
                    )}
                  </Box>
                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    {restKeys.map((c) => (
                      <Box key={c} sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 92, fontWeight: 600 }}>
                          {isRTL ? arabicColumnLabel(c) : prettyHeader(c)}
                        </Typography>
                        {isUrlValue(row[c]) ? (
                          <Box component="a" href={String(row[c]).trim()} target="_blank" rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem', color: 'primary.main', textDecoration: 'underline' }}>
                            {isPdfUrl(String(row[c])) ? <PictureAsPdf sx={{ fontSize: 14 }} />
                              : isImageUrl(String(row[c])) ? <ImageIcon sx={{ fontSize: 14 }} />
                                : <LinkIcon sx={{ fontSize: 14 }} />}
                            {fileLabel(String(row[c]))}
                          </Box>
                        ) : relatedPath(c, row) ? (
                          <Box component="span"
                            onClick={(e) => { e.stopPropagation(); const p = relatedPath(c, row); if (p) navigate(p); }}
                            sx={{ fontSize: '0.75rem', color: 'primary.main', textDecoration: 'underline dotted', cursor: 'pointer' }}>
                            {renderValue(row[c])}
                          </Box>
                        ) : typeof row[c] === 'boolean' ? (
                          <Chip
                            size="small"
                            label={row[c] ? t('agent.yes', { defaultValue: 'Yes' }) : t('agent.no', { defaultValue: 'No' })}
                            color={row[c] ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ) : enumChip(c, row[c], isRTL ? 'ar' : 'en') ? (
                          <Chip
                            size="small"
                            label={enumChip(c, row[c], isRTL ? 'ar' : 'en')!.label}
                            color={enumChip(c, row[c], isRTL ? 'ar' : 'en')!.color}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                            {isDateField(c) ? formatDate(row[c], isRTL ? 'ar' : 'en', 'medium') : renderValue(row[c])}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : (
      <Box
        sx={{
          width: '100%',
          // Cap height; DataGrid scrolls internally for larger result sets.
          height: Math.min(440, 116 + gridRows.length * 38),
          minHeight: 170,
          bgcolor: 'background.paper',
        }}
      >
        <DataGrid
          rows={gridRows}
          columns={gridColumns}
          getRowId={(row) => row.__rowId}
          density="comfortable"
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          localeText={isRTL ? arSD.components.MuiDataGrid.defaultProps.localeText : undefined}
          onRowClick={(params) => { const p = buildPath(params.row); if (p) navigate(p); }}
          sx={{
            border: 0,
            ...(canView && { '& .MuiDataGrid-row': { cursor: 'pointer' } }),
            '& .MuiDataGrid-cell': { fontSize: '0.85rem' },
            '& .MuiDataGrid-columnHeader': { fontSize: '0.78rem', fontWeight: 700, bgcolor: 'action.hover' },
            '& .MuiDataGrid-columnHeaders': { borderBottom: 1, borderColor: 'divider' },
          }}
        />
      </Box>
      )}
    </Paper>
  );
};

export default AgentResultTable;
